import React, { useState } from 'react';
import { Mail, Users, HelpCircle, Waves, CheckCircle2, Loader2, Send } from 'lucide-react';
import { TurnstileWidget } from './security/TurnstileWidget';
import { saveContactInquiry, trackEvent } from '../lib/firebase';

interface ContactSectionProps {
  contactRef: React.RefObject<HTMLDivElement | null>;
}

export const ContactSection: React.FC<ContactSectionProps> = ({ contactRef }) => {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const data = {
      name: (formData.get('name') as string) || '',
      email: (formData.get('email') as string) || '',
      organization: (formData.get('organization') as string) || '',
      inquiryType: (formData.get('inquiryType') as string) || 'General',
      message: (formData.get('message') as string) || '',
      turnstileToken: turnstileToken,
      formType: 'contact'
    };

    trackEvent('contact_form_submit', { type: data.inquiryType });

    try {
      // 1. Direct Firestore Persistence
      await saveContactInquiry({
        name: data.name,
        email: data.email,
        organization: data.organization,
        inquiryType: data.inquiryType,
        message: data.message,
        turnstileToken: data.turnstileToken,
      });

      // 2. Dispatch to Cloudflare Edge API (relative or api.uvpulser.com)
      try {
        const endpoint = window.location.hostname.includes('uvpulser') ? '/contact' : 'https://api.uvpulser.com/contact';
        await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Turnstile-Token': turnstileToken
          },
          body: JSON.stringify(data)
        });
      } catch (workerErr) {
        console.info('Worker notice: lead registered in cloud database.');
      }

      setFormSuccess(true);
      trackEvent('contact_form_success');
    } catch (err) {
      setFormError('Unable to transmit inquiry. Please email juan@afoundry.ai directly.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    saveContactInquiry({
      name: 'Newsletter Subscriber',
      email: newsletterEmail,
      inquiryType: 'Newsletter',
      message: 'Subscribed to technical updates'
    });
    setNewsletterSubscribed(true);
    trackEvent('newsletter_subscribe');
  };

  return (
    <section ref={contactRef} className="section-flowing bg-[#F6F7F9] py-20 z-[120]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Card */}
          <div className="contact-form-card uv-card p-8 bg-white shadow-sm border border-slate-200/80">
            <h2 className="text-3xl md:text-4xl font-light mb-4 text-slate-900">Let's build cleaner water systems.</h2>
            <p className="text-[#6B7280] mb-8 text-sm leading-relaxed">
              Tell us about your project specifications. We will recommend the appropriate pulsed module, flow geometry, and integration layout.
            </p>
            
            {formSuccess ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-4 text-cyan-600">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-medium mb-2 text-slate-900">Inquiry Received</h4>
                <p className="text-[#6B7280] text-sm max-w-sm mx-auto">
                  Thank you for contacting UV Pulser. Our technical engineering team will review your requirements and respond within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5 text-slate-700">Name *</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl border border-[rgba(11,12,15,0.1)] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5 text-slate-700">Email *</label>
                    <input 
                      type="email" 
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-xl border border-[rgba(11,12,15,0.1)] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-slate-700">Organization / Institution</label>
                  <input 
                    type="text" 
                    name="organization"
                    className="w-full px-4 py-2.5 text-sm rounded-xl border border-[rgba(11,12,15,0.1)] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30"
                    placeholder="Company, university, or lab"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-slate-700">Inquiry Type</label>
                  <select name="inquiryType" className="w-full px-4 py-2.5 text-sm rounded-xl border border-[rgba(11,12,15,0.1)] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 bg-white text-slate-800">
                    <option value="consumer">Consumer product / Portable wand</option>
                    <option value="industrial">Industrial rack-mount module</option>
                    <option value="technical">Technical inquiry / Spec verification</option>
                    <option value="partnership">Distributor / OEM partnership</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5 text-slate-700">Message & Application Context</label>
                  <textarea 
                    name="message"
                    rows={3}
                    className="w-full px-4 py-2.5 text-sm rounded-xl border border-[rgba(11,12,15,0.1)] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 resize-none"
                    placeholder="Describe flow rates, target pathogen inactivation, or testing environment..."
                  />
                </div>

                {/* Cloudflare Turnstile Bot Security Widget */}
                <TurnstileWidget onVerify={(token) => setTurnstileToken(token)} />

                {formError && (
                  <p className="text-red-500 text-xs">{formError}</p>
                )}

                <button 
                  type="submit" 
                  disabled={formSubmitting} 
                  className="uv-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 text-sm py-3"
                >
                  {formSubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Verifying & Sending...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Request a Technical Demo</>
                  )}
                </button>
              </form>
            )}
          </div>
          
          {/* Info Cards */}
          <div className="space-y-4">
            {/* Email - Masked with Interactive Reveal */}
            <div 
              className="contact-info-card uv-card p-6 flex items-center gap-4 cursor-pointer hover:translate-y-[-2px] transition-transform bg-white border border-slate-200/80"
              onClick={() => setEmailRevealed(true)}
            >
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center text-cyan-600 flex-shrink-0">
                <Mail className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-slate-900 text-sm">Direct Engineering & Sales</h3>
                {emailRevealed ? (
                  <a href="mailto:juan@afoundry.ai" className="text-sm text-[#06B6D4] font-mono hover:underline">
                    juan@afoundry.ai
                  </a>
                ) : (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    </div>
                    <span className="text-xs text-slate-400">@</span>
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    </div>
                    <span className="text-xs text-[#06B6D4] font-medium ml-1.5">(click to reveal)</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="contact-info-card uv-card p-6 flex items-center gap-4 hover:translate-y-[-2px] transition-transform bg-white border border-slate-200/80">
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center text-cyan-600 flex-shrink-0">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900 text-sm">Commercial Partnerships</h3>
                <p className="text-xs text-[#6B7280] mt-0.5">OEM integration, water utility pilots, and distributor agreements.</p>
              </div>
            </div>
            
            <div className="contact-info-card uv-card p-6 flex items-center gap-4 hover:translate-y-[-2px] transition-transform bg-white border border-slate-200/80">
              <div className="w-12 h-12 rounded-full bg-[#06B6D4]/10 flex items-center justify-center text-cyan-600 flex-shrink-0">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900 text-sm">Technical Support Center</h3>
                <p className="text-xs text-[#6B7280] mt-0.5">Lab test validation, fluence dose protocols, and CAD models.</p>
              </div>
            </div>
            
            <div className="contact-info-card uv-card p-6 bg-white border border-slate-200/80">
              <div className="flex items-center gap-2.5 mb-3 text-slate-900">
                <Waves className="w-4 h-4 text-[#06B6D4]" />
                <span className="font-medium text-sm">UV Tech Dispatches</span>
              </div>
              {newsletterSubscribed ? (
                <p className="text-xs text-cyan-700 bg-cyan-50 p-2.5 rounded-xl flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-600" /> You're subscribed to technical whitepapers.
                </p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                  <input 
                    type="email" 
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    className="flex-1 px-3.5 py-2 rounded-xl border border-[rgba(11,12,15,0.1)] focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30 text-xs"
                    placeholder="Enter email for research briefs"
                  />
                  <button type="submit" className="uv-btn-primary py-2 px-3.5 text-xs">
                    Subscribe
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
